import { QUERY_KEYS } from '@/constants/query';
import { FundingChartInterval, KlineInterval } from '@synfutures/sdks-perp-datasource';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSDK } from '../web3/hook';
import {
  fetchAbcFaucetSendTx,
  fetchFuturesInstrument,
  fetchFuturesInstrumentInquire,
  fetchFuturesInstrumentInquireByNotional,
  fetchFuturesInstrumentInquireByTick,
  fetchFuturesPairDepthChart,
  fetchFuturesPairFundingChart,
  fetchFuturesPairKlineChart,
  fetchFuturesPairOrderBook,
} from './api';

export const useFetchFuturesInstrument = (chainId: number | undefined, instrumentAddr: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.FUTURES.INSTRUMENT(chainId, instrumentAddr),
    queryFn: async () => {
      if (!chainId || !instrumentAddr) return null;
      try {
        // Try API first
        const apiResult = await fetchFuturesInstrument({ chainId, address: instrumentAddr });
        if (apiResult) return apiResult;
      } catch (error) {
        console.warn('API failed, falling back to chain:', error);
      }

      // Fallback to chain - this will be removed in future updates
      return null;
    },
    enabled: !!chainId && !!instrumentAddr,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useFetchFuturesOrderbook = (
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
  stepRatio?: number,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.FUTURES.ORDER_BOOK(chainId, instrumentAddr, expiry, stepRatio),
    queryFn: async () => {
      if (!chainId || !instrumentAddr || !expiry) return null;
      try {
        const result = await fetchFuturesPairOrderBook({
          chainId,
          address: instrumentAddr,
          expiry,
        });
        return result;
      } catch (error) {
        console.error('Failed to fetch order book:', error);
        return null;
      }
    },
    enabled: !!chainId && !!instrumentAddr && !!expiry,
    staleTime: 1000 * 10, // 10 seconds
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useFetchFuturesDepthChart = (
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
) => {
  const sdk = useSDK(chainId);
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.FUTURES.DEPTH_CHARTS(chainId, instrumentAddr, expiry),
    queryFn: async () => {
      if (!chainId || !instrumentAddr || !expiry || !sdk) return null;
      return fetchFuturesPairDepthChart({ chainId, address: instrumentAddr, expiry, sdk });
    },
    enabled: !!chainId && !!instrumentAddr && !!expiry && !!sdk,
    staleTime: 1000 * 10, // 10 seconds
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useFetchFuturesFundingChart = (
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
  chartDuration: FundingChartInterval | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.FUTURES.FUNDING_CHARTS(chainId, instrumentAddr, expiry, chartDuration),
    queryFn: async () => {
      if (!chainId || !instrumentAddr || !expiry || !chartDuration) return null;
      return fetchFuturesPairFundingChart({ chainId, instrumentAddr: instrumentAddr, expiry, chartDuration });
    },
    enabled: !!chainId && !!instrumentAddr && !!expiry && !!chartDuration,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useFetchFuturesKlineChart = ({
  chainId,
  instrumentAddr,
  expiry,
  chartDuration,
  timeEnd,
}: {
  chainId?: number;
  instrumentAddr?: string;
  expiry?: number;
  chartDuration?: KlineInterval;
  timeEnd?: number;
}) => {
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.FUTURES.KLINE_CHARTS({
      chainId,
      instrumentAddress: instrumentAddr,
      expiry,
      chartDuration,
      timeEnd,
    }),
    queryFn: async () => {
      if (!chainId || !instrumentAddr || !expiry || !chartDuration) return null;
      return fetchFuturesPairKlineChart({
        chainId,
        instrumentAddress: instrumentAddr,
        expiry,
        klineInterval: chartDuration,
        timeEnd,
      });
    },
    enabled: !!chainId && !!instrumentAddr && !!expiry && !!chartDuration,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useFetchFuturesInstrumentInquire = (
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
  size: string | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.FUTURES.INSTRUMENT_INQUIRE(chainId, instrumentAddr, expiry, size),
    queryFn: async () => {
      if (!chainId || !instrumentAddr || !expiry || !size) return null;
      return fetchFuturesInstrumentInquire({ chainId, instrument: instrumentAddr, expiry, size });
    },
    enabled: !!chainId && !!instrumentAddr && !!expiry && !!size,
    staleTime: 1000 * 10, // 10 seconds
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useFetchFuturesInstrumentInquireByTick = (
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
  tick: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.FUTURES.INSTRUMENT_INQUIRE_BY_TICK(chainId, instrumentAddr, expiry, tick),
    queryFn: async () => {
      if (!chainId || !instrumentAddr || !expiry || tick === undefined) return null;
      return fetchFuturesInstrumentInquireByTick({ chainId, instrument: instrumentAddr, expiry, tick });
    },
    enabled: !!chainId && !!instrumentAddr && !!expiry && tick !== undefined,
    staleTime: 1000 * 10, // 10 seconds
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useFetchFuturesInstrumentInquireByNotional = (
  chainId: number | undefined,
  instrumentAddr: string | undefined,
  expiry: number | undefined,
  notional: string | undefined,
  long: boolean | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.FUTURES.INSTRUMENT_INQUIRE_BY_NOTIONAL(chainId, instrumentAddr, expiry, notional, long),
    queryFn: async () => {
      if (!chainId || !instrumentAddr || !expiry || !notional || long === undefined) return null;
      return fetchFuturesInstrumentInquireByNotional({
        chainId,
        instrument: instrumentAddr,
        expiry,
        notional,
        long,
      });
    },
    enabled: !!chainId && !!instrumentAddr && !!expiry && !!notional && long !== undefined,
    staleTime: 1000 * 10, // 10 seconds
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useAbcFaucetSendTx = () => {
  return useMutation({
    mutationFn: fetchAbcFaucetSendTx,
  });
};
